import { Launch } from '@carbon/icons-react';
import { message, Spin } from 'antd';
import { EMDASH } from 'constants';
import { useRef, useState } from 'react';
import { familyService, providerService } from 'services';

export const GetFileContentsEntityTypes = {
  PROVIDER_APPLICATION: 'providerApplication',
  PROVIDER_ORGANIZATION: 'providerOrganization',
  PROVIDER_CARE_FACILITY: 'providerCareFacility',
  FAMILY: 'family',
};

export const FileList = ({ applicationId, careFacilityId, organizationId, entityType, files }) => {
  const [previewLoading, setPreviewLoading] = useState(null);

  // Check if props provided are invalid
  const reportedInvalidEntityTypeError = useRef(false);
  const reportedMissingApplicationIdError = useRef(false);
  const reportedMissingCareFacilityIdError = useRef(false);
  const reportedMissingOrganizationIdError = useRef(false);

  // Check if entityType is missing
  if (
    !reportedInvalidEntityTypeError.current &&
    (!entityType || !Object.values(GetFileContentsEntityTypes).includes(entityType))
  ) {
    console.error('Entity type is required to show file previews');
    reportedInvalidEntityTypeError.current = true;
    return null;
  }
  // Check if applicationId is missing for family or provider application
  if (
    (entityType === GetFileContentsEntityTypes.FAMILY ||
      entityType === GetFileContentsEntityTypes.PROVIDER_APPLICATION) &&
    applicationId === undefined
  ) {
    console.error('Application ID is required to show file previews');
    reportedMissingApplicationIdError.current = true;
    return null;
  }
  // Check if careFacilityId is missing for provider care facility
  if (entityType === GetFileContentsEntityTypes.PROVIDER_CARE_FACILITY && careFacilityId === undefined) {
    console.error('Care facility ID is required to show file previews');
    reportedMissingCareFacilityIdError.current = true;
    return null;
  }
  // Check if organizationId is missing for provider organization
  if (entityType === GetFileContentsEntityTypes.PROVIDER_ORGANIZATION && organizationId === undefined) {
    console.error('Organization ID is required to show file previews');
    reportedMissingOrganizationIdError.current = true;
    return null;
  }

  if (!files || files.length === 0) {
    return EMDASH;
  }

  return (
    <div className="flex flex-col gap-2">
      {files?.map((file) => {
        const fileId = file.uid || file.id;

        return (
          <Spin spinning={previewLoading === fileId} key={fileId}>
            <button
              onClick={(e) => {
                e.preventDefault();

                showFilePreview({
                  applicationId,
                  careFacilityId,
                  organizationId,
                  file,
                  entityType,
                  setLoading: setPreviewLoading,
                });
              }}
              aria-label={`Preview ${file.name}`}
              className="underline text-primary p-0 cursor-pointer text-left leading-relaxed"
            >
              <Launch className="shrink-0 align-middle mr-1 mt-[-1px] ml-[-1px]" />

              {file.name}
            </button>
          </Spin>
        );
      })}
    </div>
  );
};

export const showFilePreview = async ({
  applicationId,
  careFacilityId,
  organizationId,
  file,
  entityType,
  setLoading,
}) => {
  const fileId = file.uid || file.id;
  setLoading?.(fileId);

  try {
    let fileData;

    if (entityType === GetFileContentsEntityTypes.FAMILY) {
      fileData = await familyService.getApplicationFileContents(applicationId, fileId);
    } else if (entityType === GetFileContentsEntityTypes.PROVIDER_APPLICATION) {
      fileData = await providerService.getApplicationFileContents(applicationId, fileId);
    } else if (entityType === GetFileContentsEntityTypes.PROVIDER_ORGANIZATION) {
      fileData = await providerService.getOrgFileContents(organizationId, fileId);
    } else if (entityType === GetFileContentsEntityTypes.PROVIDER_CARE_FACILITY) {
      fileData = await providerService.getCareFacilityFileContents(careFacilityId, fileId);
    } else {
      message.error('Invalid entity type for file preview');
      setLoading?.(null);

      return;
    }

    const url = `data:${fileData.contentType};base64,${fileData.contents}`;

    if (fileData) {
      setLoading?.(null);

      const _htmlContent = fileData.contentType.startsWith('image/')
        ? `<html>
            <body style="margin:0;display:flex;align-items:center;justify-content:center;background-color:black;">
              <img src="${url}" style="max-width:100%; max-height:100%;" alt="${file.name} - preview" />
            </body>
          </html>`
        : `<html>
            <body style="margin:0;">
              <object data="${url}" type="application/pdf" width="100%" height="100%">
              </object>
            </body>
          </html>`;

      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(_htmlContent);
      } else {
        // If the popup was blocked, show a Modal
        message.error('Please allow popups for this site to view the file preview');
      }
    } else {
      message.error(`Unable to retrieve preview data for file: ${file.name}`);
      console.log('Error while retrieving preview data', fileData);

      setLoading?.(null);
    }
  } catch (e) {
    setLoading?.(null);

    message.error(`Unable to retrieve preview data for file: ${file.name}`);
    console.error('Error while generating preview', e);
  }
};
